import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// dev/app-skeleton/src/pages/Search.tsx
// 本地搜索：解密后按 title + body 文本字段做大小写不敏感子串匹配。
// 高亮：把命中片段包 <mark> 渲染（无 HTML 注入：用 React 段切片，不 dangerouslySetInnerHTML）。
// 性能：所有解密走 entry-cache LRU；entries 量 < 10k 时无压力。未来 R+ 上 fuse.js / 倒排索引。
// D14-DEV-v2 / SEC C' 修：删 plaintext_json 旁路；明文从 decryptEntriesCached 返回的 pt 字段拿。
import { useLiveQuery } from "dexie-react-hooks";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { db } from "../lib/db";
import { decryptEntriesCached } from "../lib/entry-cache";
import { useCurrentUserId } from "../lib/current-user";
function unwrapBodyText(pt) {
    if (!pt)
        return "";
    const doc = pt.body_tiptap_json;
    if (!doc?.content)
        return "";
    return doc.content
        .map((p) => (p.content ?? []).map((n) => (n.type === "text" ? n.text ?? "" : "")).join(""))
        .join("\n");
}
function pickSnippet(text, q, radius = 40) {
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0)
        return text.slice(0, radius * 2);
    const start = Math.max(0, i - radius);
    const end = Math.min(text.length, i + q.length + radius);
    return (start > 0 ? "…" : "") + text.slice(start, end) + (end < text.length ? "…" : "");
}
function Highlight({ text, q }) {
    if (!q)
        return _jsx(_Fragment, { children: text });
    const lc = text.toLowerCase();
    const qlc = q.toLowerCase();
    const parts = [];
    let i = 0;
    while (i < text.length) {
        const j = lc.indexOf(qlc, i);
        if (j < 0) {
            parts.push({ s: text.slice(i), hit: false });
            break;
        }
        if (j > i)
            parts.push({ s: text.slice(i, j), hit: false });
        parts.push({ s: text.slice(j, j + q.length), hit: true });
        i = j + q.length;
    }
    return (_jsx(_Fragment, { children: parts.map((p, k) => p.hit
            ? _jsx("mark", { className: "bg-accent/40 text-text rounded-sm px-0.5", "data-testid": "hl", children: p.s }, k)
            : _jsx("span", { children: p.s }, k)) }));
}
export function SearchPage() {
    const [q, setQ] = useState("");
    const userId = useCurrentUserId();
    const all = useLiveQuery(async () => {
        if (!userId)
            return null;
        const rows = await db.entries.orderBy("updated_at").reverse().filter((e) => !e.deleted).toArray();
        return decryptEntriesCached(userId, rows);
    }, [userId]);
    const hits = useMemo(() => {
        if (!q.trim() || !all)
            return [];
        const qlc = q.trim().toLowerCase();
        return all.flatMap(({ row, pt }) => {
            if (!pt)
                return []; // 解密失败的行跳过（不参与搜索，避免 false negative 静默；UI 主列表会显示"已加密"）
            const title = pt.title ?? "";
            const body = unwrapBodyText(pt);
            const matchInTitle = title.toLowerCase().includes(qlc);
            const matchInBody = body.toLowerCase().includes(qlc);
            if (!matchInTitle && !matchInBody)
                return [];
            const snippet = matchInBody ? pickSnippet(body, q) : body.slice(0, 80);
            const matchCount = (title.toLowerCase().split(qlc).length - 1) +
                (body.toLowerCase().split(qlc).length - 1);
            return [{ entry: row, title: title || "(无标题)", snippet, matchCount }];
        });
    }, [q, all]);
    return (_jsxs("section", { className: "px-md py-lg max-w-3xl mx-auto", "data-testid": "page-search", children: [_jsxs("header", { className: "mb-md", children: [_jsx("h1", { className: "text-2xl font-medium mb-sm", children: "\u641C\u7D22" }), _jsx("input", { type: "search", autoFocus: true, placeholder: "\u641C\u7D22\u6807\u9898\u4E0E\u6B63\u6587\u2026", value: q, onChange: (e) => setQ(e.target.value), className: "card w-full px-md py-sm bg-surface text-text", style: { minHeight: 44 }, "data-testid": "search-input", "aria-label": "\u641C\u7D22" })] }), !q.trim() ? (_jsx("p", { className: "text-text-muted text-sm", children: "\u8F93\u5165\u5173\u952E\u8BCD\u5F00\u59CB\u641C\u7D22 \u00B7 \u4EC5\u672C\u673A\u89E3\u5BC6\u5185\u5BB9\u53C2\u4E0E\u5339\u914D\u3002" })) : !hits.length ? (_jsx("div", { className: "card p-lg text-text-muted text-sm", "data-testid": "search-empty", children: "\u6CA1\u6709\u5339\u914D\u7684\u65E5\u8BB0\u3002" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "text-text-muted text-sm mb-sm", "data-testid": "search-count", children: [hits.length, " \u6761\u5339\u914D"] }), _jsx("ul", { className: "flex flex-col gap-sm", "data-testid": "search-results", children: hits.map((h) => (_jsx("li", { children: _jsxs(Link, { to: `/entry/${h.entry.id}`, className: "card p-md block hover:bg-surface-2", style: { minHeight: 44 }, "data-testid": `search-row-${h.entry.id}`, children: [_jsxs("div", { className: "text-xs text-text-muted", children: [new Date(h.entry.updated_at).toLocaleString("zh-CN"), " \u00B7 ", h.matchCount, " \u5904\u547D\u4E2D"] }), _jsx("div", { className: "font-medium mt-xs", children: _jsx(Highlight, { text: h.title, q: q }) }), _jsx("div", { className: "text-sm text-text-muted mt-xs whitespace-pre-line", children: _jsx(Highlight, { text: h.snippet, q: q }) })] }) }, h.entry.id))) })] }))] }));
}
